import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { useParams, useNavigate } from 'react-router-dom'
import { isToday, isFuture, differenceInDays } from 'date-fns'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import CheckboxField from '@/v2/components/common/CheckboxField'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import { useTreatments } from '@/v2/hooks/treatments/useTreatments'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { usePatient } from '@/v2/hooks/patient/usePatient'
import { useGetDoctors } from '@/v2/hooks/useDoctors'
import { baseURL, headers } from '@/v2/constants/requests'
import {
  getDateTimeWithoutTimezone,
  nowOnClinicTimezone,
  safeFormat,
} from '@/v2/utils/convert'
import { parseHtmlToString } from '@/v2/utils/helpers'
import useModals from '@/v2/hooks/useModals'

const Wrapper = styled.div`
  padding: 0;
`

const TopBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px 0;
`

const RescheduleCard = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  margin: 0 16px 12px 16px;
  background: transparent;
  border: 1px dashed #c4c4c4;
  border-radius: 12px;
`

const RescheduleDateBlock = styled.div`
  min-width: 96px;
`

const RescheduleBody = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
`

const RescheduleTitle = styled.div`
  font-size: 14px;
  font-style: italic;
  color: #333;
`

const RescheduleAuthor = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  margin: 12px 16px;
  background: #e8f4fc;
  border-radius: 50px;
  cursor: pointer;
  gap: 8px;
`

const SectionTitle = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`

const AppointmentCard = styled.div`
  padding: 16px;
  margin: 0 16px 12px 16px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const DateText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`

const TimeText = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 2px;
`

const TodayBadge = styled.div`
  display: inline-block;
  background: #d32f2f;
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-top: 8px;
  text-transform: uppercase;
`

const AppointmentType = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-top: 8px;
`

const DoctorRoom = styled.div`
  font-size: 13px;
  color: #666;
  margin-top: 4px;
`

const NoteSection = styled.div`
  padding: 16px;
  background: #fafafa;
  border-left: 3px solid #e0e0e0;
  margin: 0 16px 16px 16px;
`

const NoteInput = styled(TextField)`
  && {
    width: 100%;
    background: #fff;

    .MuiOutlinedInput-root {
      border-radius: 8px;
    }
  }
`

const NoteActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #666;
`

const TreatmentNoteCard = styled.div`
  padding: 16px;
  margin: 0 16px 12px 16px;
  background: ${({ $classification }) =>
    $classification === 'green' ? '#E7F2CF' : '#FFF6BE'};
  border-radius: 12px;
`

const TreatmentNoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const TreatmentNoteDate = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`

const TreatmentNoteTime = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`

const TreatmentNoteContent = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  margin: 12px 0;
`

const TreatmentNoteAuthor = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #555;
`

const FAB = styled.button`
  position: fixed;
  bottom: 100px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: #1976d2;
  color: #fff;
  border: none;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 100;
  transition: all 0.2s ease;

  &:active {
    transform: scale(0.95);
  }

  svg {
    font-size: 28px;
  }
`

const NotesToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  cursor: pointer;
  color: #1976d2;
  font-size: 14px;
  font-weight: 500;
`

const NotesList = styled.div`
  margin-top: 12px;
`

const NoteCard = styled.div`
  background: #f8f9fa;
  border-left: 3px solid #1976d2;
  padding: 12px;
  margin-bottom: 8px;
  border-radius: 0 8px 8px 0;

  &:last-child {
    margin-bottom: 0;
  }
`

const NoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`

const NoteDate = styled.div`
  font-size: 12px;
  color: #666;
`

const NoteContent = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  margin-bottom: 8px;
`

const NoteAuthor = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #555;
`

const TreatmentMobile = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { goTo } = useModals()
  const { onAddNote, onUpdateNote, onDeleteNote } = usePatientContext()
  const { patient } = usePatient(id)
  const { treatments, isLoading } = useTreatments(id)
  const { onOpenSnackbar } = useSnackbarContext()
  const { data: doctors } = useGetDoctors()

  const [upcomingExpanded, setUpcomingExpanded] = useState(true)
  const [pastExpanded, setPastExpanded] = useState(true)
  const [notesExpanded, setNotesExpanded] = useState(true)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const [selectedAppointment, setSelectedAppointment] = useState(null)
  const [noteText, setNoteText] = useState('')
  const [activeNoteAppointment, setActiveNoteAppointment] = useState(null)
  const [expandedNotes, setExpandedNotes] = useState({})
  const [fabMenuAnchor, setFabMenuAnchor] = useState(null)
  const [noteMenuAnchor, setNoteMenuAnchor] = useState(null)
  const [selectedNote, setSelectedNote] = useState(null)
  const [filterAnchor, setFilterAnchor] = useState(null)
  const [showReschedules, setShowReschedules] = useState(true)

  const toggleNotes = appointmentId => {
    setExpandedNotes(prev => ({
      ...prev,
      [appointmentId]: !prev[appointmentId],
    }))
  }

  // Filter appointments and notes
  const visibleTreatments =
    (showReschedules
      ? treatments
      : treatments?.filter(item => item.type !== 'reschedule')) || []
  const appointments = visibleTreatments.filter(item => !item.classification)
  const allNotes = visibleTreatments.filter(item => item.classification)

  // Separate upcoming and past — reschedule events are historical, so they
  // always go to past.
  const upcoming = appointments.filter(apt => {
    if (apt.type === 'reschedule') return false
    const date = new Date(getDateTimeWithoutTimezone(apt.startsAt))
    return isFuture(date) || isToday(date)
  })

  const past = appointments.filter(apt => {
    if (apt.type === 'reschedule') return true
    const date = new Date(getDateTimeWithoutTimezone(apt.startsAt))
    return !isFuture(date) && !isToday(date)
  })

  // FAB menu handlers
  const handleFabClick = event => {
    setFabMenuAnchor(event.currentTarget)
  }

  const handleFabMenuClose = () => {
    setFabMenuAnchor(null)
  }

  const handleAddYellowNote = () => {
    goTo(`/v2/patient/${id}/notes/new/yellow`, {
      onSave: note => onAddNote(note),
    })
    handleFabMenuClose()
  }

  const handleAddAppointment = () => {
    handleFabMenuClose()
    goTo('/v2/event/new', {
      start: nowOnClinicTimezone(),
      patient,
    })
  }

  // Note menu handlers
  const handleNoteMenuOpen = (event, note) => {
    event.stopPropagation()
    setNoteMenuAnchor(event.currentTarget)
    setSelectedNote(note)
  }

  const handleNoteMenuClose = () => {
    setNoteMenuAnchor(null)
    setSelectedNote(null)
  }

  const handleEditNote = () => {
    if (selectedNote) {
      goTo(`/v2/patient/${id}/notes/${selectedNote.id}`, {
        note: selectedNote,
        onSave: note => onUpdateNote(note),
      })
    }
    handleNoteMenuClose()
  }

  const handleDeleteNote = async () => {
    if (selectedNote && confirm('Are you sure you want to delete this note?')) {
      try {
        const isYellowNote = selectedNote.classification === 'yellow'
        const url = isYellowNote
          ? `${baseURL}/v2/rest/patients/${id}/treatments/notes/${selectedNote.id}.json`
          : `${baseURL}/v2/rest/patients/${id}/notes/${selectedNote.id}`
        const response = await fetch(url, {
          method: 'DELETE',
          headers,
        })
        if (response.ok) {
          onDeleteNote(selectedNote.id)
          onOpenSnackbar('Note deleted.')
        } else {
          onOpenSnackbar('Failed to delete note.', 'error')
        }
      } catch (error) {
        onOpenSnackbar('Something went wrong!', 'error')
      }
    }
    handleNoteMenuClose()
  }

  const handleMenuOpen = (event, appointment) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setSelectedAppointment(appointment)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
    setSelectedAppointment(null)
  }

  const handleViewAppointment = () => {
    if (selectedAppointment) {
      // Use navigate directly for fullscreen routes on mobile
      navigate(`/v2/appointment/${selectedAppointment.id}`)
    }
    handleMenuClose()
  }

  const handleEditAppointment = () => {
    if (selectedAppointment) {
      // Use navigate directly for fullscreen routes on mobile
      navigate(`/v2/appointment/${selectedAppointment.id}/edit`)
    }
    handleMenuClose()
  }

  const handleAddNote = appointment => {
    setActiveNoteAppointment(appointment)
    setNoteText('')
  }

  const handleCancelNote = () => {
    setActiveNoteAppointment(null)
    setNoteText('')
  }

  const handleSaveNote = async () => {
    if (noteText.trim() && activeNoteAppointment) {
      const doctor = doctors?.[0]
      if (!doctor) {
        onOpenSnackbar('Please create a doctor first.', 'error')
        return
      }
      try {
        const response = await fetch(
          `${baseURL}/v2/rest/patients/${id}/treatments/appointments/${activeNoteAppointment.id}/notes`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              doctor_id: doctor.id,
              annotation: noteText,
            }),
          }
        )
        if (response.ok) {
          queryClient.invalidateQueries({ queryKey: ['Treatments', `${id}`] })
          onOpenSnackbar('Note created.')
          setActiveNoteAppointment(null)
          setNoteText('')
        } else {
          onOpenSnackbar('Failed to create note.', 'error')
        }
      } catch (error) {
        onOpenSnackbar('Something went wrong.', 'error')
      }
    }
  }

  if (isLoading) {
    return (
      <Wrapper>
        <EmptyState>Loading...</EmptyState>
      </Wrapper>
    )
  }

  const renderAppointment = appointment => {
    const date = new Date(getDateTimeWithoutTimezone(appointment.startsAt))
    const isAppointmentToday = isToday(date)
    const notes = appointment.notes || []
    const notesCount = notes.length
    const isNotesExpanded = expandedNotes[appointment.id]
    const currentDate = nowOnClinicTimezone()

    return (
      <React.Fragment key={appointment.id}>
        <AppointmentCard>
          <CardHeader>
            <div>
              <DateText>{safeFormat(date, 'MMMM d, yyyy')}</DateText>
              <TimeText>{safeFormat(date, 'hh:mm a')}</TimeText>
              {isAppointmentToday && (
                <TodayBadge>Today's Appointment</TodayBadge>
              )}
              <AppointmentType>
                {appointment.appointmentType?.title || 'Appointment'}
              </AppointmentType>
              <DoctorRoom>
                {appointment.doctor && `Dr. ${appointment.doctor.fullName}`}
                {appointment.doctor && appointment.room && ' - '}
                {appointment.room &&
                  `Room ${appointment.room?.name ||
                    appointment.room?.title ||
                    ''}`}
              </DoctorRoom>
            </div>
            <IconButton
              size="small"
              onClick={e => handleMenuOpen(e, appointment)}
            >
              <MoreVertIcon />
            </IconButton>
          </CardHeader>

          {notesCount > 0 && (
            <>
              <NotesToggle onClick={() => toggleNotes(appointment.id)}>
                {isNotesExpanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
                {notesCount} Note{notesCount > 1 ? 's' : ''}
              </NotesToggle>

              {isNotesExpanded && (
                <NotesList>
                  {notes.map(note => {
                    const noteDate = getDateTimeWithoutTimezone(note.createdAt)
                    const daysOld = differenceInDays(currentDate, noteDate)
                    const isEditable = daysOld < 1

                    return (
                      <NoteCard key={note.id}>
                        <NoteHeader>
                          <NoteDate>
                            {safeFormat(noteDate, 'MMM d, yyyy')} at{' '}
                            {safeFormat(noteDate, 'hh:mm a')}
                          </NoteDate>
                          {isEditable && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                goTo(
                                  `/v2/patient/${id}/notes/${note.id}/edit`,
                                  {
                                    appointmentId: appointment.id,
                                    note,
                                  }
                                )
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </NoteHeader>
                        <NoteContent>
                          {parseHtmlToString(note.annotation)}
                        </NoteContent>
                        <NoteAuthor>
                          Dr. {note.user?.fullName || 'Not Assigned'}
                        </NoteAuthor>
                      </NoteCard>
                    )
                  })}
                </NotesList>
              )}
            </>
          )}
        </AppointmentCard>

        {activeNoteAppointment?.id === appointment.id && (
          <NoteSection>
            <NoteInput
              placeholder="Write a new note..."
              multiline
              rows={2}
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              variant="outlined"
              size="small"
            />
            <NoteActions>
              <Button variant="text" onClick={handleCancelNote}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSaveNote}
                disabled={!noteText.trim()}
              >
                Save
              </Button>
            </NoteActions>
          </NoteSection>
        )}
      </React.Fragment>
    )
  }

  const renderReschedule = event => {
    const date = getDateTimeWithoutTimezone(event.rescheduledAt)
    const author = event.user?.fullName || 'system'
    const title = event.appointment?.appointmentType?.title || 'Appointment'

    return (
      <RescheduleCard key={`reschedule-${event.id}`}>
        <RescheduleDateBlock>
          <DateText>{safeFormat(date, 'MMM d, yyyy')}</DateText>
          <TimeText>{safeFormat(date, 'hh:mm a')}</TimeText>
        </RescheduleDateBlock>
        <RescheduleBody>
          <EventRepeatIcon fontSize="small" sx={{ color: '#666' }} />
          <div>
            <RescheduleTitle>{`Rescheduled — ${title}`}</RescheduleTitle>
            <RescheduleAuthor>{`by ${author}`}</RescheduleAuthor>
          </div>
        </RescheduleBody>
      </RescheduleCard>
    )
  }

  const renderNote = note => {
    const noteDate = getDateTimeWithoutTimezone(note.createdAt)

    return (
      <TreatmentNoteCard key={note.id} $classification={note.classification}>
        <TreatmentNoteHeader>
          <div>
            <TreatmentNoteDate>
              {safeFormat(noteDate, 'MMMM d, yyyy')}
            </TreatmentNoteDate>
            <TreatmentNoteTime>
              {safeFormat(noteDate, 'hh:mm a')}
            </TreatmentNoteTime>
          </div>
          <IconButton size="small" onClick={e => handleNoteMenuOpen(e, note)}>
            <MoreVertIcon />
          </IconButton>
        </TreatmentNoteHeader>
        <TreatmentNoteContent>
          {parseHtmlToString(note.annotation)}
        </TreatmentNoteContent>
        <TreatmentNoteAuthor>
          Dr. {note.user?.fullName || 'Not Assigned'}
        </TreatmentNoteAuthor>
      </TreatmentNoteCard>
    )
  }

  return (
    <Wrapper>
      <TopBar>
        <IconButton
          aria-label="Treatment options"
          aria-haspopup="true"
          onClick={e => setFilterAnchor(e.currentTarget)}
          sx={{ padding: '12px' }}
        >
          <MoreVertIcon />
        </IconButton>
      </TopBar>

      {/* Upcoming Section */}
      {upcoming.length > 0 && (
        <>
          <SectionHeader onClick={() => setUpcomingExpanded(!upcomingExpanded)}>
            <SectionTitle>{upcoming.length} Upcoming</SectionTitle>
            {upcomingExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </SectionHeader>
          {upcomingExpanded && upcoming.map(renderAppointment)}
        </>
      )}

      {/* Past Section */}
      {past.length > 0 && (
        <>
          <SectionHeader onClick={() => setPastExpanded(!pastExpanded)}>
            <SectionTitle>{past.length} Past</SectionTitle>
            {pastExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </SectionHeader>
          {pastExpanded &&
            past.map(item =>
              item.type === 'reschedule'
                ? renderReschedule(item)
                : renderAppointment(item)
            )}
        </>
      )}

      {/* Notes Section */}
      {allNotes.length > 0 && (
        <>
          <SectionHeader onClick={() => setNotesExpanded(!notesExpanded)}>
            <SectionTitle>
              {allNotes.length} Note{allNotes.length > 1 ? 's' : ''}
            </SectionTitle>
            {notesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </SectionHeader>
          {notesExpanded && allNotes.map(renderNote)}
        </>
      )}

      {appointments.length === 0 && allNotes.length === 0 && (
        <EmptyState>No appointments or notes</EmptyState>
      )}

      {/* Appointment Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewAppointment}>View Details</MenuItem>
        <MenuItem onClick={handleEditAppointment}>Edit Appointment</MenuItem>
        <MenuItem
          onClick={() => {
            handleAddNote(selectedAppointment)
            handleMenuClose()
          }}
        >
          Add Note
        </MenuItem>
      </Menu>

      {/* Note Menu */}
      <Menu
        anchorEl={noteMenuAnchor}
        open={Boolean(noteMenuAnchor)}
        onClose={handleNoteMenuClose}
      >
        <MenuItem onClick={handleEditNote}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteNote}>Delete</MenuItem>
      </Menu>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box px={2} py={1}>
          <CheckboxField
            label="Show rescheduled appointments"
            checked={showReschedules}
            onChange={e => setShowReschedules(e.target.checked)}
          />
        </Box>
      </Menu>

      {/* FAB Button */}
      <FAB onClick={handleFabClick}>
        <AddIcon />
      </FAB>

      {/* FAB Menu */}
      <Menu
        anchorEl={fabMenuAnchor}
        open={Boolean(fabMenuAnchor)}
        onClose={handleFabMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MenuItem onClick={handleAddAppointment}>Add Appointment</MenuItem>
        <MenuItem onClick={handleAddYellowNote}>Add Yellow Note</MenuItem>
      </Menu>
    </Wrapper>
  )
}

export default TreatmentMobile
